import { Signal } from '@latitude/signal';
import { cn } from '@latitude/token-utils';
import type { Ref } from 'react';
import type { BadgeProps, BadgeSize, BadgeVariant } from 'types';

import { badgeVariants } from './variants/badge-variants';
import { iconVariants } from './variants/icon-variants';
import { textAreaVariants } from './variants/text-variants';

const Badge = ({
  className,
  variant = 'info',
  size = 'medium',
  truncate = true,
  leadingElement = undefined,
  trailingElement = undefined,
  signal = undefined,
  children,
  rawProps = {},
  'data-testid': testID = 'badge',
  ref,
}: BadgeProps & {
  ref?: Ref<HTMLDivElement>;
}) => {
  const { className: signalClassName, ...signalRest } = signal ?? {};

  return (
    <div
      data-testid={testID}
      className={cn(
        badgeVariants({
          variant,
          size,
          truncate,
        }),
        // Conditional: an unconditional `relative` would capture any
        // absolutely-positioned content consumers pass through the slots.
        signal && 'relative',
        className,
      )}
      ref={ref}
      {...rawProps}
    >
      {leadingElement && (
        <span className={cn(iconVariants())}>{leadingElement}</span>
      )}
      {(children || children === 0) && (
        <span className={cn(textAreaVariants({ size, truncate }))}>
          {children}
        </span>
      )}
      {trailingElement && (
        <span className={cn(iconVariants())}>{trailingElement}</span>
      )}
      {/* Offsets match button/icon-button. They resolve against the padding
          box, so 4px also clears the 2px border, leaving a 2px overhang.
          `border` is not forced here -- the ring is box-content, so it would
          overshoot the designed 8px dot. */}
      {signal && (
        <Signal
          {...signalRest}
          className={cn([
            'pointer-events-none',
            'absolute',
            '-right-sizing-sys-size-4',
            '-top-sizing-sys-size-4',
            signalClassName,
          ])}
        />
      )}
    </div>
  );
};

Badge.displayName = 'Badge';

export { Badge, type BadgeProps, type BadgeVariant, type BadgeSize };
